/**
 * Each department dashboard's engine — the block that's its own (docs/plans/scores.md "Dashboards") — as plain data
 * for the phone and the web to draw: Sales the chain, Marketing the piece in motion, Finance the queue to zero, Dev the
 * ship line, Field on site now, General find it a home, Personal its projects. Pure — no I/O.
 * `data` is what deptGoalsView reads (tasks, projects, departments, departmentMode, metrics, cfo, receipts, sales, finance, now).
 */
import { goalValue, goalWindow, homeSuggestion, deptRole, jobBurn, looseReceiptCount } from './deptgoals.js';
import { workDepartments } from './workdept.js';
import { departmentOf } from './hierarchy.js';
import { addDaysStr } from './phonestats.js';
import { localDateStr } from './time.js';
import { canonTag } from './tags.js';
import { quickTasksFor, rankQuickTasks } from './quicktasks.js';

const DAY = 86400000;
const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const open = (t) => t && t.status !== 'done' && !t.deletedAt;
const tagged = (t, tag) => Array.isArray(t?.tags) && t.tags.some((x) => canonTag(String(x)) === tag);
const doneOn = (t) => (t?.status === 'done' && t.completedAt ? localDateStr(t.completedAt) : null);
const ageDays = (t, now) => (num(t?.createdAt) ? Math.max(0, Math.floor((now - num(t.createdAt)) / DAY)) : null);

/**
 * Sales — outreach → leads → assessments → estimates → won this month, the % that carries from each row to the next
 * and the biggest drop. Leads / estimates / wins count this month from the books' Sales feed (booksfeed.js, S4) once
 * it's on the device (`source: 'feed'`, with the open pipeline and days lead → quote → won); until then they're the
 * CFO view's last 90 days by lead source (`source: 'books'`).
 */
export function salesChain(dept, data = {}) {
  const now = data.now || Date.now();
  const month = goalWindow('month', now);
  const v = (key) => goalValue({ key }, dept, month, data).value;
  const hasOutreach = (data.metrics || []).some((m) => m.id === 'outreach');
  const feed = !!data.sales?.ready;
  const books = data.cfo?.leads?.bySource || null;
  const sum = (k) => (books ? books.reduce((a, r) => a + (num(r[k]) || 0), 0) : null);
  const fromBooks = !feed && !!books;
  const rows = [
    { key: 'outreach', label: 'Outreach', value: hasOutreach ? v('metric:outreach') : null, note: hasOutreach ? null : 'add the counter', src: 'month' },
    { key: 'leads', label: 'Leads', value: feed ? v('sales:leads') : sum('leads'), src: fromBooks ? '90d' : 'month' },
    { key: 'assessments', label: 'Assessments', value: v('tag:assessment'), src: 'month' },
    { key: 'estimates', label: 'Estimates', value: feed ? v('sales:estimatesSent') : sum('bids'), src: fromBooks ? '90d' : 'month' },
    { key: 'won', label: 'Won', value: feed ? v('sales:won') : sum('won'), strong: true, src: fromBooks ? '90d' : 'month' },
  ];
  // The % that carries from one row to the next — only between rows counted over the same span (this month's
  // outreach next to the books' 90 days would read nonsense).
  const drops = rows.slice(0, -1).map((r, i) => {
    const a = num(r.value); const b = num(rows[i + 1].value);
    return r.src === rows[i + 1].src && a > 0 && b != null ? Math.round((b / a) * 100) : null;
  });
  let worst = null;
  drops.forEach((x, i) => { if (x != null && (worst == null || x < drops[worst])) worst = i; });
  const pipeline = feed ? { count: data.sales.pipeline.count, value: data.sales.pipeline.value, stages: data.sales.pipeline.stages.map((s) => ({ stage: s.stage, count: num(s.count) || 0, value: num(s.value) || 0 })) } : null;
  return { rows, drops, worst, source: feed ? 'feed' : books ? 'books' : null, hasOutreach, pipeline, avgDays: feed ? data.sales.avgDays : null };
}

/**
 * Marketing — the piece in motion (the #content task touched last), its steps (subtasks), the next one, a 14-day
 * "one step a day" strip, and channels from the books ranked by profit.
 */
export function contentPiece(data = {}) {
  const now = data.now || Date.now();
  const tasks = data.tasks || [];
  const content = tasks.filter((t) => tagged(t, 'content') && !t.deletedAt);
  const lastTouch = (t) => Math.max(num(t.updatedAt) || 0, num(t.createdAt) || 0, ...(t.timeEntries || []).map((e) => num(e.createdAt) || 0));
  const piece = content.filter((t) => open(t) && !t.parentId).sort((a, b) => lastTouch(b) - lastTouch(a))[0] || null;
  const steps = piece ? tasks.filter((t) => t.parentId === piece.id && !t.deletedAt).sort((a, b) => (num(a.order) ?? 0) - (num(b.order) ?? 0)) : [];
  const nextStep = steps.find(open) || null;
  const today = localDateStr(now);
  const ids = new Set(content.map((t) => t.id));
  const active = new Set();
  for (const t of tasks) {
    if (!ids.has(t.id) && !ids.has(t.parentId)) continue;
    const d = doneOn(t); if (d) active.add(d);
    for (const e of t.timeEntries || []) if (e.date && e.duration > 0) active.add(e.date);
  }
  const days = Array.from({ length: 14 }, (_, i) => { const date = addDaysStr(today, i - 13); return { date, active: active.has(date) }; });
  const channels = [...(data.cfo?.leads?.bySource || [])].filter((r) => r && r.source !== 'unknown')
    .sort((a, b) => (num(b.profit) || 0) - (num(a.profit) || 0)).slice(0, 5)
    .map((r) => ({ source: r.source, name: r.name || r.source, leads: num(r.leads) || 0, costPerLead: num(r.costPerLead), profit: num(r.profit) || 0 }));
  return { piece, steps, nextStep, days, hit: days.filter((d) => d.active).length, channels };
}

/**
 * Finance — the queue to zero (receipts with no job, overdue to chase, filings due in 30 days, GST owing), with what's
 * left, and cash: bank, runway in weeks, the forecast's low week and each week's balance. With the books' Finance feed
 * (booksfeed.js, S4) on the device the queue is the books' own: receipts to post, bank to reconcile (each with how many
 * are over 7 days old), overdue invoices by count — and money collected this week / month.
 */
export function financeQueue(data = {}) {
  const now = data.now || Date.now();
  const cfo = data.cfo;
  const cash = cfo?.cash || null;
  const today = localDateStr(now);
  const in30 = addDaysStr(today, 30);
  const fin = data.finance?.ready ? data.finance : null;
  // filings: the CFO view's, else the Finance feed's (not done) — a Finance member may have the feed without the CFO doc
  const filingRows = cfo?.compliance?.rows || (fin ? fin.filings.filter((r) => r.status !== 'done') : []);
  const filings = filingRows.filter((r) => r.due && r.due <= in30);
  const late = filings.filter((r) => r.due < today);
  const overdue = num(cash?.arOverdue?.value ?? cash?.arOverdue);
  const gst = num(cash?.gstOwing?.value ?? cash?.gstOwing);
  const queue = (q, none) => (q.count ? `${q.over7 ? `${q.over7} over 7 days · ` : ''}oldest ${q.oldest}` : none);
  const od = fin ? fin.ar.overdue : overdue;
  const rows = [
    fin ? { key: 'receipts', title: 'Receipts to post', sub: queue(fin.receipts, 'all posted'), late: fin.receipts.over7 > 0, count: fin.receipts.count, link: null }
      : { key: 'receipts', title: 'Receipts with no job', sub: 'assign them in BattleBooks → Receipts', count: looseReceiptCount(data.receipts), link: null },
    ...(fin ? [{ key: 'bank', title: 'Bank to reconcile', sub: queue(fin.bank, 'all reconciled'), late: fin.bank.over7 > 0, count: fin.bank.count, link: null }] : []),
    { key: 'overdue', title: 'Overdue to chase', sub: cash || fin ? `$${Math.round(od || 0).toLocaleString('en-CA')} past due${fin?.ar.overdueCount ? ` · ${fin.ar.overdueCount} invoice${fin.ar.overdueCount === 1 ? '' : 's'}` : ''}` : 'waiting on the books', count: fin ? fin.ar.overdueCount : overdue > 0 ? 1 : 0, link: 'cfo' },
    { key: 'filings', title: 'Filings due · 30 days', sub: late.length ? `${late.length} late` : filings[0] ? `${filings[0].label} · ${filings[0].due}` : 'nothing due', late: late.length > 0, count: filings.length, link: 'cfo' },
    { key: 'gst', title: 'GST owing', sub: gst != null ? `$${gst.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—', count: gst > 0 ? 1 : 0, link: 'cfo' },
  ];
  const low = cfo?.forecast?.lowest || null;
  const weeks = (cfo?.forecast?.weeks || []).map((w) => ({ week: w.week, from: w.from, balance: num(w.balance) || 0, low: !!low && w.week === low.week }));
  return {
    rows, left: rows.reduce((a, r) => a + (r.count || 0), 0), ready: !!cfo?.ready || !!fin,
    collected: fin ? { week: num(fin.totals?.week?.collected) || 0, month: num(fin.totals?.month?.collected) || 0 } : null,
    cash: cash ? { bank: num(cash.bank?.value ?? cash.bank), runwayWeeks: cash.runwayMonths == null ? null : Math.round(cash.runwayMonths * 4.345), low } : null,
    weeks,
  };
}

/** Dev — built but not live (#deploy, open), live this week (#shipped, done), your bug list (#bug, open, oldest first). */
export function shipLine(data = {}) {
  const now = data.now || Date.now();
  const tasks = data.tasks || [];
  const wk = goalWindow('week', now);
  const waiting = tasks.filter((t) => open(t) && tagged(t, 'deploy')).map((t) => ({ task: t, ageDays: ageDays(t, now) }));
  const live = tasks.filter((t) => tagged(t, 'shipped') && doneOn(t) && doneOn(t) >= wk.from && doneOn(t) <= wk.to)
    .sort((a, b) => (num(b.completedAt) || 0) - (num(a.completedAt) || 0)).map((t) => ({ task: t, date: doneOn(t) }));
  const bugs = tasks.filter((t) => open(t) && tagged(t, 'bug')).sort((a, b) => (num(a.createdAt) || 0) - (num(b.createdAt) || 0)).map((t) => ({ task: t, ageDays: ageDays(t, now) }));
  return { waiting, live, bugs };
}

/**
 * The running timer as a time entry (Lj, Oct 11: "the running session MUST count live, same as the clock bar"): the
 * stretch since the task's timer started (pauses taken out — computeCardActiveTime's `ms`) added to the running task
 * as an entry dated today, marked `live`. Every hours figure that reads entries then counts it. → tasks (same array
 * when nothing runs)
 */
export function withLiveTime(tasks = [], { taskId = null, ms = 0, date = null } = {}) {
  if (!taskId || !(ms > 0) || !date) return tasks;
  return tasks.map((t) => (t.id === taskId ? { ...t, timeEntries: [...(t.timeEntries || []), { id: 'live', date, duration: ms, live: true }] } : t));
}

/**
 * Dev's engine on Home (Lj, Oct 11: "for Dev, goals will mostly be task completions / feature rollouts … improvements
 * made, issues fixed, what project we spent the most time on — specific to what we are doing"; "charts everywhere a
 * number lives"). One shape for the phone and the web. Each block counts exactly what its preset goal counts
 * (goalValue — so the card and the score never disagree) and says what it counted and left out (`counted`, `leftOut`):
 *   shipped        done #shipped tasks          (tag:shipped)
 *   fixed          done #bug tasks              (tag:bug)
 *   open           open #bug tasks              (open:bug)       — the oldest first
 *   improvements   done #improvement tasks      (tag:improvement)
 *   waiting        open #deploy tasks           (open:deploy)    — built, not live, each with its age
 *   time           hours on Dev tasks this week by project (hoursWorked) — top 3, and each day Mon → Sun by project
 * A done block: n (this week, Mon → today), prev (the same days last week), n30 (last 30 days), spark (each of the
 * last 7 days). An open block: n (now), prev (7 days ago), spark (open at the end of each of the last 7 days — rebuilt
 * from made / finished dates). No #feature / #rollout tags exist in the suite's vocabulary, so a rollout is #shipped.
 */
export function devEngine(dept, data = {}) {
  const now = data.now || Date.now();
  const d = { ...data, now };
  const today = localDateStr(now);
  const wk = goalWindow('week', now);
  const span = (from, to) => ({ period: 'week', from, to, end: to, share: 1, off: false });
  const prevWk = span(addDaysStr(wk.from, -7), addDaysStr(wk.to, -7));
  const last7 = Array.from({ length: 7 }, (_, i) => addDaysStr(today, i - 6));
  const tasks = data.tasks || [];
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const wd = workDepartments({ tasks, projects: data.projects || [], departments: data.departments || [], departmentMode: data.departmentMode ?? true });
  const isDev = (t) => wd.forTask(t)?.id === dept?.id;
  const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
  const name = String(dept?.name || 'Dev');

  // this department's own finished work this week (not checklists / quick tasks / routine steps) — what an untagged
  // finish looks like, for "left out"
  const mineDone = tasks.filter((t) => !t.deletedAt && !t.bucket && !t.quickTaskId && !t.runId && doneOn(t) && doneOn(t) >= wk.from && doneOn(t) <= wk.to && isDev(t));
  const count = (key, w) => goalValue({ key }, dept, w, d).value || 0;
  const doneTag = (slug, { untaggedNote = true } = {}) => {
    const key = `tag:${slug}`;
    const got = goalValue({ key }, dept, wk, d);
    const rows = (got.rows || []).map((r) => ({ task: byId.get(r.taskId), date: r.date })).filter((x) => x.task)
      .sort((a, b) => (num(b.task.completedAt) || 0) - (num(a.task.completedAt) || 0));
    const outside = rows.filter((r) => !isDev(r.task)).length;
    const untagged = mineDone.filter((t) => !tagged(t, slug)).length;
    const leftOut = [];
    if (untagged && untaggedNote) leftOut.push(`${plural(untagged, `${name} task`)} finished this week without #${slug} — not counted`);
    const days = (goalValue({ key }, dept, span(last7[0], today), d).rows || []).map((r) => r.date);
    return {
      n: rows.length, rows, prev: count(key, prevWk), n30: count(key, span(addDaysStr(today, -29), today)),
      spark: last7.map((date) => ({ date, value: days.filter((x) => x === date).length })),
      counted: `${plural(rows.length, 'task')} tagged #${slug} finished this week${outside ? ` (${outside} outside ${name})` : ''}`, leftOut,
    };
  };
  const openTag = (slug) => {
    const got = goalValue({ key: `open:${slug}` }, dept, wk, d);
    const rows = (got.rows || []).map((r) => ({ task: byId.get(r.taskId), ageDays: r.ageDays })).filter((x) => x.task);
    const outside = rows.filter((r) => !isDev(r.task)).length;
    // open at the end of a day: made by then (no made date = always), not finished by then, not deleted, not skipped
    const all = tasks.filter((t) => !t.deletedAt && t.status !== 'skipped' && tagged(t, slug));
    const openAt = (date) => all.filter((t) => (!num(t.createdAt) || localDateStr(num(t.createdAt)) <= date) && !(doneOn(t) && doneOn(t) <= date)).length;
    return {
      n: rows.length, rows, oldest: rows[0] || null, prev: openAt(addDaysStr(today, -7)),
      spark: last7.map((date) => ({ date, value: date === today ? rows.length : openAt(date) })),
      counted: `${got.basis}${outside ? ` · ${outside} outside ${name}` : ''}`, leftOut: got.notes || [],
    };
  };

  // hours on this department's tasks this week, by project and by day — the same entries the Dev hours goal adds up
  const byProject = new Map();
  const perDay = new Map();   // date → Map(pid → ms)
  let total = 0; let noProject = 0; let liveMs = 0;
  for (const t of tasks) {
    for (const e of t.timeEntries || []) {
      if (!e?.date || e.date < wk.from || e.date > wk.to || !((e.duration || 0) > 0) || wd.forEntry(e, t)?.id !== dept?.id) continue;
      total += e.duration;
      if (e.live) liveMs += e.duration;
      const pid = t.projectId || (t.parentId ? byId.get(t.parentId)?.projectId : null) || null;
      if (!perDay.has(e.date)) perDay.set(e.date, new Map());
      perDay.get(e.date).set(pid || '', (perDay.get(e.date).get(pid || '') || 0) + e.duration);
      if (!pid) { noProject += e.duration; continue; }
      byProject.set(pid, (byProject.get(pid) || 0) + e.duration);
    }
  }
  const projById = new Map((data.projects || []).map((p) => [p.id, p]));
  const ranked = [...byProject].sort((a, b) => b[1] - a[1]).map(([pid, ms]) => ({ project: projById.get(pid) || { id: pid, name: 'A project' }, ms, share: total ? ms / total : 0 }));
  const top = ranked.slice(0, 3);
  const rest = ranked.slice(3);
  // the chart's series: the 4 busiest projects in their own colours, the rest (and no project) as Other
  const series = ranked.slice(0, 4).map((r) => ({ id: r.project.id, name: r.project.name, color: r.project.color || null, ms: r.ms }));
  const keep = new Set(series.map((x) => x.id));
  const otherMs = total - series.reduce((a, x) => a + x.ms, 0);
  if (otherMs > 0) series.push({ id: 'other', name: ranked.length > 4 ? 'Other' : 'No project', color: null, ms: otherMs });
  const week = Array.from({ length: 7 }, (_, i) => {
    const date = addDaysStr(wk.from, i);
    const m = perDay.get(date) || new Map();
    const parts = series.map((x) => ({ id: x.id, ms: x.id === 'other' ? [...m].filter(([pid]) => !keep.has(pid)).reduce((a, [, v]) => a + v, 0) : m.get(x.id) || 0 }));
    return { date, future: date > today, parts, ms: parts.reduce((a, p) => a + p.ms, 0) };
  });
  const hrs = (ms) => `${Math.round((ms / 3600000) * 10) / 10} h`;
  const timeLeft = [];
  if (rest.length) timeLeft.push(`${plural(rest.length, 'more project')} · ${hrs(rest.reduce((a, r) => a + r.ms, 0))} not in the top 3`);
  if (noProject) timeLeft.push(`${hrs(noProject)} on tasks with no project — in the total, not ranked`);
  timeLeft.push(liveMs ? `includes the running timer (${Math.round(liveMs / 60000)} min so far)` : 'clock time with no task running isn’t counted');

  return {
    week: { from: wk.from, to: wk.to },
    shipped: doneTag('shipped'),
    fixed: doneTag('bug', { untaggedNote: false }),   // a finish with no #bug isn't a missed fix
    open: openTag('bug'),
    improvements: doneTag('improvement'),
    time: { totalMs: total, liveMs, rows: top, series, days: week, counted: total ? `${hrs(total)} on ${name} tasks this week · ${plural(ranked.length, 'project')}` : `no time on ${name} tasks this week yet`, leftOut: total ? timeLeft : [] },
    waiting: openTag('deploy'),
  };
}

/**
 * Dev's dashboard (Lj, Oct 11: "polished and premium — give us ALL the data for the Dev department, it's a dashboard";
 * the All Home is the bar). Everything over the last 14 days (today included) from the device, the running timer
 * included when the caller passed it in (withLiveTime). Builds on devEngine (the week's counts = the goals' counts).
 * → { days, engine, hours: { today, avg, perDay, series, todayByProject, week, lastWeek, weeks, liveMs },
 *     done: { today, perDay, avg }, shipped: { week, avg4, weeks }, issues: { openPerDay, opened14, fixed14, byProject },
 *     openByProject, plan: { days, avg, streak } }
 */
export function devDashboard(dept, data = {}) {
  const now = data.now || Date.now();
  const today = localDateStr(now);
  const engine = devEngine(dept, { ...data, now });
  const tasks = data.tasks || [];
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const projById = new Map((data.projects || []).map((p) => [p.id, p]));
  const wd = workDepartments({ tasks, projects: data.projects || [], departments: data.departments || [], departmentMode: data.departmentMode ?? true });
  const isDev = (t) => wd.forTask(t)?.id === dept?.id;
  const pidOf = (t) => t?.projectId || (t?.parentId ? byId.get(t.parentId)?.projectId : null) || null;
  const days = Array.from({ length: 14 }, (_, i) => addDaysStr(today, i - 13));
  const wk = goalWindow('week', now);
  const weekStarts = Array.from({ length: 8 }, (_, i) => addDaysStr(wk.from, (i - 7) * 7));

  // hours: every entry run under this department (the Dev hours goal's entries)
  const perDayProj = new Map(days.map((d) => [d, new Map()]));
  const perWeek = new Map(weekStarts.map((w) => [w, 0]));
  const tot14 = new Map();
  let liveMs = 0;
  for (const t of tasks) {
    for (const e of t.timeEntries || []) {
      if (!e?.date || !((e.duration || 0) > 0) || wd.forEntry(e, t)?.id !== dept?.id) continue;
      if (e.live) liveMs += e.duration;
      const ws = weekStarts.find((w) => e.date >= w && e.date <= addDaysStr(w, 6));
      if (ws) perWeek.set(ws, perWeek.get(ws) + e.duration);
      const m = perDayProj.get(e.date);
      if (!m) continue;
      const pid = pidOf(t) || '';
      m.set(pid, (m.get(pid) || 0) + e.duration);
      tot14.set(pid, (tot14.get(pid) || 0) + e.duration);
    }
  }
  const ranked = [...tot14].filter(([pid]) => pid).sort((a, b) => b[1] - a[1]);
  const series = ranked.slice(0, 5).map(([pid, ms]) => ({ id: pid, name: projById.get(pid)?.name || 'A project', color: projById.get(pid)?.color || null, icon: projById.get(pid)?.icon || null, ms }));
  const keep = new Set(series.map((x) => x.id));
  const otherMs = [...tot14].filter(([pid]) => !keep.has(pid)).reduce((a, [, v]) => a + v, 0);
  if (otherMs > 0) series.push({ id: 'other', name: ranked.length > 5 ? 'Other / no project' : 'No project', color: null, ms: otherMs });
  const perDay = days.map((date) => {
    const m = perDayProj.get(date);
    const parts = series.map((x) => ({ id: x.id, ms: x.id === 'other' ? [...m].filter(([pid]) => !keep.has(pid)).reduce((a, [, v]) => a + v, 0) : m.get(x.id) || 0 }));
    return { date, parts, ms: parts.reduce((a, p) => a + p.ms, 0) };
  });
  const todayMs = perDay[13].ms;
  const worked = perDay.slice(0, 13).filter((d) => d.ms > 0);
  const todayByProject = series.map((x) => ({ ...x, ms: perDay[13].parts.find((p) => p.id === x.id)?.ms || 0 })).filter((x) => x.ms > 0);

  // finished work: this department's tasks (not checklists / quick tasks / routine steps) by the day they were done
  const mine = tasks.filter((t) => !t.deletedAt && !t.bucket && !t.quickTaskId && !t.runId && isDev(t));
  const doneDays = days.map((date) => mine.filter((t) => doneOn(t) === date && !t.parentId).length);

  // shipped per week (8 weeks) — the tag:shipped goal's count for each week
  const span = (from, to) => ({ period: 'week', from, to, end: to, share: 1, off: false });
  const shippedWeeks = weekStarts.map((w) => ({ from: w, n: goalValue({ key: 'tag:shipped' }, dept, span(w, w === wk.from ? today : addDaysStr(w, 6)), { ...data, now }).value || 0 }));
  const prior4 = shippedWeeks.slice(3, 7);

  // issues over 14 days: opened (made) and fixed (finished) per day; open at the end of each day; open by project
  const bugs = tasks.filter((t) => !t.deletedAt && t.status !== 'skipped' && tagged(t, 'bug'));
  const madeOn = (t) => (num(t.createdAt) ? localDateStr(num(t.createdAt)) : null);
  const openAt = (date) => bugs.filter((t) => (!madeOn(t) || madeOn(t) <= date) && !(doneOn(t) && doneOn(t) <= date)).length;
  const openNow = bugs.filter((t) => open(t));
  const byProjectCount = (list) => {
    const m = new Map();
    for (const t of list) { const pid = pidOf(t) || ''; m.set(pid, (m.get(pid) || 0) + 1); }
    return [...m].sort((a, b) => b[1] - a[1]).map(([pid, n]) => ({ project: pid ? projById.get(pid) || { id: pid, name: 'A project' } : { id: '', name: 'No project' }, n }));
  };

  // the plan, this department only: tasks dated each day, and how many of them were done that day
  const planDays = days.map((date) => {
    const planned = mine.filter((t) => !t.parentId && t.scheduledDate === date);
    const hit = planned.filter((t) => doneOn(t) === date).length;
    return { date, planned: planned.length, done: hit, rate: planned.length ? Math.round((hit / planned.length) * 100) : null };
  });
  const rated = planDays.filter((d) => d.rate != null);
  let streak = 0;
  for (let i = 13; i >= 0; i -= 1) { if (doneDays[i] > 0) streak += 1; else if (i < 13) break; }

  return {
    days, engine,
    hours: {
      today: todayMs, avg: worked.length ? worked.reduce((a, d) => a + d.ms, 0) / worked.length : 0, perDay, series, todayByProject, liveMs,
      week: perWeek.get(wk.from) || 0, lastWeek: perWeek.get(weekStarts[6]) || 0, weeks: weekStarts.map((w) => ({ from: w, ms: perWeek.get(w) || 0 })),
    },
    done: { today: doneDays[13], perDay: days.map((date, i) => ({ date, n: doneDays[i] })), avg: doneDays.slice(0, 13).reduce((a, b) => a + b, 0) / 13 },
    shipped: { week: engine.shipped.n, avg4: prior4.reduce((a, w) => a + w.n, 0) / 4, weeks: shippedWeeks },
    issues: {
      openPerDay: days.map((date) => ({ date, n: date === today ? openNow.length : openAt(date) })),
      opened14: bugs.filter((t) => madeOn(t) && madeOn(t) >= days[0]).length,
      fixed14: bugs.filter((t) => doneOn(t) && doneOn(t) >= days[0]).length,
      byProject: byProjectCount(openNow),
    },
    openByProject: byProjectCount(mine.filter((t) => open(t) && t.status !== 'skipped' && !t.parentId)),
    plan: { days: planDays, avg: rated.length ? Math.round(rated.reduce((a, d) => a + d.rate, 0) / rated.length) : null, streak },
  };
}

/**
 * The Working-on card on a department's Home (Lj, Oct 11: "the Working on card isn't department specific"): only this
 * department's planned tasks, in plan order. The running task leads when it's this department's; when it's another's,
 * it becomes a small "Clocked on …" line and this department's next task leads. `plan` = the day plan in order (fresh
 * first, then carried). → { next, running, elsewhere: { task, project, dept } | null, queue, hidden }
 */
export function deptUpNext(dept, { plan = [], running = null, tasks = [], projects = [], departments = [], departmentMode = true, size = 4 } = {}) {
  const wd = workDepartments({ tasks, projects, departments, departmentMode });
  const mine = (t) => wd.forTask(t)?.id === dept?.id;
  const runMine = running && mine(running) ? running : null;
  const list = plan.filter((t) => t && t.id !== running?.id);
  const here = list.filter(mine);
  const next = runMine || here[0] || null;
  const queue = here.filter((t) => t.id !== next?.id).slice(0, size);
  return {
    next, running: runMine,
    elsewhere: running && !runMine ? { task: running, project: projects.find((p) => p.id === running.projectId) || null, dept: wd.forTask(running) || null } : null,
    queue, hidden: list.length - here.length,
  };
}

// ── a department's pinned projects (Lj, Oct 11: "we should be able to add app projects to the home screen", "give
// the cards like info for that app too") ──────────────────────────────────────────────────────────────────────────

const LIVE = (p) => p && !p.deletedAt && !p.archived && !['archived', 'completed', 'cancelled'].includes(p.status);
/** A clean pin list: unique strings, in order, at most 12. */
export const cleanHomeProjectIds = (ids) => [...new Set((Array.isArray(ids) ? ids : []).filter((x) => typeof x === 'string' && x))].slice(0, 12);
/** Pin a project to the end (already pinned: unchanged). */
export const pinHomeProject = (ids, id) => { const l = cleanHomeProjectIds(ids); return !id || l.includes(id) ? l : cleanHomeProjectIds([...l, id]); };
/** Unpin — the project itself is never touched. */
export const unpinHomeProject = (ids, id) => cleanHomeProjectIds(ids).filter((x) => x !== id);
/** Move a pin one place (dir −1 = earlier, +1 = later). */
export function moveHomeProject(ids, id, dir) {
  const l = cleanHomeProjectIds(ids); const i = l.indexOf(id); const j = i + (dir < 0 ? -1 : 1);
  if (i < 0 || j < 0 || j >= l.length) return l;
  [l[i], l[j]] = [l[j], l[i]];
  return l;
}

/** The picker: this department's live projects not pinned yet, then every other live project. → { here, others } */
export function homeProjectChoices(dept, data = {}) {
  const pinned = new Set(cleanHomeProjectIds(dept?.homeProjectIds));
  const live = (data.projects || []).filter((p) => LIVE(p) && !pinned.has(p.id)).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  const here = live.filter((p) => departmentOf(p, data.departments || []).id === dept?.id);
  const ids = new Set(here.map((p) => p.id));
  return { here, others: live.filter((p) => !ids.has(p.id)) };
}

/**
 * One project's information card — ops data only, counted the way the Dev cards count (goalValue for the tags, time
 * entries for the hours), narrowed to the project (a subtask follows its parent's project), this week (Monday → today):
 * hours this week + the running task when it's on this project, open tasks (top level, no checklists), open #bug,
 * shipped this week (#shipped done), built not live (#deploy open), the next task on the schedule (overdue first) and
 * hours each day of the last 7. → { project, weekMs, running, open, bugs, shipped, waiting, next, days }
 */
export function projectCard(project, data = {}, { activeTaskId = null, role = 'dev' } = {}) {
  if (role !== 'dev') return projectsCard(project, data, { activeTaskId });
  const now = data.now || Date.now();
  const today = localDateStr(now);
  const wk = goalWindow('week', now);
  const tasks = data.tasks || [];
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const pidOf = (t) => t?.projectId || (t?.parentId ? byId.get(t.parentId)?.projectId : null) || null;
  const mine = (t) => pidOf(t) === project?.id;
  const d = { ...data, now };
  const tag = (key, w = wk) => (goalValue({ key }, null, w, d).rows || []).map((r) => byId.get(r.taskId)).filter((t) => t && mine(t));
  const from7 = addDaysStr(today, -6);
  const perDay = new Map();
  let weekMs = 0;
  for (const t of tasks) {
    if (!mine(t) || t.deletedAt) continue;
    for (const e of t.timeEntries || []) {
      if (!e?.date || !((e.duration || 0) > 0)) continue;
      if (e.date >= wk.from && e.date <= wk.to) weekMs += e.duration;
      if (e.date >= from7 && e.date <= today) perDay.set(e.date, (perDay.get(e.date) || 0) + e.duration);
    }
  }
  const openTop = tasks.filter((t) => t.projectId === project?.id && open(t) && t.status !== 'skipped' && !t.parentId && !t.bucket && !t.quickTaskId);
  const dated = openTop.filter((t) => t.scheduledDate).sort((a, b) => String(a.scheduledDate).localeCompare(String(b.scheduledDate)) || (num(a.createdAt) || 0) - (num(b.createdAt) || 0));
  const next = dated[0] ? { task: dated[0], date: dated[0].scheduledDate, overdue: dated[0].scheduledDate < today } : null;
  const running = activeTaskId && byId.get(activeTaskId) && mine(byId.get(activeTaskId)) ? byId.get(activeTaskId) : null;
  return {
    project, role: 'dev', weekMs, running,
    open: openTop.length,
    bugs: tag('open:bug'),
    shipped: tag('tag:shipped').sort((a, b) => (num(b.completedAt) || 0) - (num(a.completedAt) || 0)),
    waiting: tag('open:deploy'),
    next,
    days: Array.from({ length: 7 }, (_, i) => { const date = addDaysStr(from7, i); return { date, ms: perDay.get(date) || 0 }; }),
  };
}

/** The department’s pinned projects, in order (a deleted project drops out), each as its information card (by role). */
export function homeProjects(dept, data = {}, opts = {}) {
  const byId = new Map((data.projects || []).map((p) => [p.id, p]));
  const role = opts.role || (homeCardRole(dept) === 'dev' ? 'dev' : 'projects');
  return cleanHomeProjectIds(dept?.homeProjectIds).map((id) => byId.get(id)).filter((p) => p && !p.deletedAt).map((p) => projectCard(p, data, { ...opts, role }));
}

// ── every department's projects are cards (Lj, Oct 11: "personal projects should be cards like the app section") ──

/** Which card a department's pinned projects wear: dev (apps) · field (jobs) · projects (everyone else). */
export function homeCardRole(dept) {
  const r = deptRole(dept);
  return r === 'dev' ? 'dev' : r === 'field' ? 'field' : 'projects';
}

const PIN_COPY = Object.freeze({
  dev: { title: 'Apps', noun: 'app', nouns: 'apps', add: 'Add an app', sub: 'pinned to this Home · hours this week (live) · click a figure for its list', first: 'Pin the apps you work on — hours, bugs, what’s next', more: 'Pin another app to this Home', picker: 'Add an app to Home', settings: 'Apps on its Home — the apps pinned to this department’s Home, in this order' },
  field: { title: 'Jobs', noun: 'job', nouns: 'jobs', add: 'Add a job', sub: 'pinned to this Home · contract, spent, margin · hours this week (live)', first: 'Pin the jobs you’re on — contract, spent, margin, hours', more: 'Pin another job to this Home', picker: 'Add a job to Home', settings: 'Jobs on its Home — the jobs pinned to this department’s Home, in this order' },
  projects: { title: 'Projects', noun: 'project', nouns: 'projects', add: 'Add a project', sub: 'pinned to this Home · hours this week (live) · click a figure for its list', first: 'Pin your projects — hours this week, open tasks, what’s next', more: 'Pin another project to this Home', picker: 'Add a project to Home', settings: 'Projects on its Home — the projects pinned to this department’s Home, in this order' },
});

/** The words the pinned section uses, by role (header, add card, picker, Settings, the empty row). */
export function homePinsCopy(dept) {
  const role = homeCardRole(dept);
  const c = PIN_COPY[role];
  const name = String(dept?.name || '').trim() || 'this department';
  return { role, ...c, empty: `No ${c.nouns} in ${name} yet` };
}

/** When a project last saw work, ms: its tasks' (subtasks follow their parent) time entries, finishes, edits, creation; else the project's own. */
export function projectLastActivity(project, data = {}) {
  const tasks = data.tasks || [];
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const pidOf = (t) => t?.projectId || (t?.parentId ? byId.get(t.parentId)?.projectId : null) || null;
  let last = 0;
  const see = (v) => { const n = num(v); if (n && n > last) last = n; };
  for (const t of tasks) {
    if (t.deletedAt || pidOf(t) !== project?.id) continue;
    see(t.completedAt); see(t.updatedAt); see(t.createdAt);
    for (const e of t.timeEntries || []) {
      if (!e?.date) continue;
      see(e.endTime ?? e.endedAt ?? e.startTime ?? new Date(`${e.date}T12:00:00`).getTime());
    }
  }
  if (!last) { see(project?.updatedAt); see(project?.createdAt); }
  return last || null;
}

/**
 * The projects pinned to a non-Dev / non-Field Home before any pin was ever set (Field's own: defaultFieldPins): the
 * department's live projects (its home department is this one — a project locked to it carries it as its department),
 * newest activity first, at most 8.
 */
export function defaultHomePins(dept, data = {}) {
  const { projects = [], departments = [] } = data;
  const mine = projects.filter((p) => LIVE(p) && departmentOf(p, departments).id === dept?.id);
  const at = new Map(mine.map((p) => [p.id, projectLastActivity(p, data) || 0]));
  return cleanHomeProjectIds(mine.sort((a, b) => at.get(b.id) - at.get(a.id) || String(a.name || '').localeCompare(String(b.name || ''))).slice(0, 8).map((p) => p.id));
}

/** Whether the department has any live project of its own (no = the compact "No projects in X yet" row). */
export const deptHasProjects = (dept, data = {}) => (data.projects || []).some((p) => LIVE(p) && departmentOf(p, data.departments || []).id === dept?.id);

/**
 * A project's card on any other department's Home (Personal, Sales, Marketing, Finance, General): hours this week (live)
 * + the running task, the last 7 days, open tasks, done this week (top level), the next task on the schedule (overdue
 * first), last activity. No bug / shipped / not-live figures — those are Dev's.
 * → { project, role: 'projects', weekMs, running, open, done, next, lastAt, days }
 */
export function projectsCard(project, data = {}, { activeTaskId = null } = {}) {
  const now = data.now || Date.now();
  const today = localDateStr(now);
  const wk = goalWindow('week', now);
  const tasks = data.tasks || [];
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const pidOf = (t) => t?.projectId || (t?.parentId ? byId.get(t.parentId)?.projectId : null) || null;
  const mine = (t) => pidOf(t) === project?.id;
  const from7 = addDaysStr(today, -6);
  const perDay = new Map();
  let weekMs = 0;
  for (const t of tasks) {
    if (!mine(t) || t.deletedAt) continue;
    for (const e of t.timeEntries || []) {
      if (!e?.date || !((e.duration || 0) > 0)) continue;
      if (e.date >= wk.from && e.date <= wk.to) weekMs += e.duration;
      if (e.date >= from7 && e.date <= today) perDay.set(e.date, (perDay.get(e.date) || 0) + e.duration);
    }
  }
  const top = tasks.filter((t) => t.projectId === project?.id && !t.deletedAt && !t.parentId && !t.bucket && !t.quickTaskId && t.status !== 'skipped');
  const openTop = top.filter(open);
  const done = top.filter((t) => { const d = doneOn(t); return d && d >= wk.from && d <= wk.to; }).sort((a, b) => (num(b.completedAt) || 0) - (num(a.completedAt) || 0));
  const dated = openTop.filter((t) => t.scheduledDate).sort((a, b) => String(a.scheduledDate).localeCompare(String(b.scheduledDate)) || (num(a.createdAt) || 0) - (num(b.createdAt) || 0));
  const next = dated[0] ? { task: dated[0], date: dated[0].scheduledDate, overdue: dated[0].scheduledDate < today } : null;
  const running = activeTaskId && byId.get(activeTaskId) && mine(byId.get(activeTaskId)) ? byId.get(activeTaskId) : null;
  const lastAt = running ? now : projectLastActivity(project, data);
  return {
    project, role: 'projects', weekMs, running,
    open: openTop.length, done, next, lastAt,
    days: Array.from({ length: 7 }, (_, i) => { const date = addDaysStr(from7, i); return { date, ms: perDay.get(date) || 0 }; }),
  };
}

/** "today" · "yesterday" · "3 d ago" · "5 wk ago" · "—" — a card's last activity, by local day. */
export function lastActivityText(at, now = Date.now()) {
  if (!num(at)) return '—';
  const a = localDateStr(num(at)); const t = localDateStr(now);
  if (a >= t) return 'today';
  const days = Math.round((new Date(`${t}T12:00:00`) - new Date(`${a}T12:00:00`)) / DAY);
  if (days <= 1) return 'yesterday';
  if (days < 14) return `${days} d ago`;
  if (days < 70) return `${Math.round(days / 7)} wk ago`;
  return `${Math.round(days / 30)} mo ago`;
}

/**
 * Field, not on site — this department's jobs with work on today (or overdue): each job, how many tasks, the first
 * one up, whether any is overdue, how far through the estimate it is (`pct`, null with no estimates) and the site
 * address. Busiest first. → [{ project, n, overdue, next, pct, address }]
 */
export function fieldTodayJobs(dept, data = {}) {
  const today = localDateStr(data.now || Date.now());
  const { tasks = [], projects = [], departments = [], departmentMode = true } = data;
  const wd = workDepartments({ tasks, projects, departments, departmentMode });
  const by = new Map();
  for (const t of tasks) {
    if (!open(t) || t.parentId || t.bucket || t.quickTaskId || !t.projectId || !t.scheduledDate || t.scheduledDate > today) continue;
    if (wd.forTask(t)?.id !== dept?.id) continue;
    const j = by.get(t.projectId) || { n: 0, overdue: 0, list: [] };
    j.n += 1; if (t.scheduledDate < today) j.overdue += 1; j.list.push(t);
    by.set(t.projectId, j);
  }
  const order = (t) => num(t.sortOrder) ?? num(t.manualOrder) ?? num(t.createdAt) ?? 0;
  const lastWorked = new Map();
  for (const t of tasks) for (const e of t.timeEntries || []) if (t.projectId && e?.date && e.date > (lastWorked.get(t.projectId) || '')) lastWorked.set(t.projectId, e.date);
  return [...by].map(([pid, j]) => {
    const project = projects.find((p) => p.id === pid) || null;
    const burn = jobBurn(tasks, pid);
    return { project, n: j.n, overdue: j.overdue, next: [...j.list].sort((a, b) => order(a) - order(b))[0] || null, pct: burn?.estMin ? Math.round(burn.doneShare * 100) : null, address: String(project?.address || '').trim() || null };
  }).filter((x) => x.project).sort((a, b) => {
    // A job with a deadline first, soonest on top (Ken sorted last with one overdue task); then what you worked last.
    const da = a.project.endDate && a.project.endDate >= today ? a.project.endDate : null;
    const db = b.project.endDate && b.project.endDate >= today ? b.project.endDate : null;
    if (da || db) { if (!da) return 1; if (!db) return -1; if (da !== db) return da.localeCompare(db); }
    return String(lastWorked.get(b.project.id) || '').localeCompare(String(lastWorked.get(a.project.id) || '')) || b.n - a.n;
  });
}

/**
 * Where Field stands right now (Lj, Oct 11: the running Dev task was shown as the Field job). On site only when the
 * work running is THIS department's: the running task resolves here (its job = the site), or no task runs and the
 * clock's job belongs here. Anything else running is `elsewhere` — "Clocked on Battle Ops · Dev".
 * → { onSite, project, running, elsewhere: { task, project, dept } | null }
 */
export function fieldPlace(dept, data = {}, { clock = null, activeTaskId = null } = {}) {
  const { tasks = [], projects = [], departments = [], departmentMode = true } = data;
  const wd = workDepartments({ tasks, projects, departments, departmentMode });
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const projOf = (pid) => (pid ? projects.find((p) => p.id === pid) || null : null);
  const running = activeTaskId ? byId.get(activeTaskId) || null : null;
  if (running) {
    const d = wd.forTask(running);
    const pid = running.projectId || (running.parentId ? byId.get(running.parentId)?.projectId : null) || null;
    if (d?.id === dept?.id) return { onSite: !!projOf(pid), project: projOf(pid), running, elsewhere: null };
    return { onSite: false, project: null, running: null, elsewhere: { task: running, project: projOf(pid), dept: d || null } };
  }
  const cp = clock?.projectId ? projOf(clock.projectId) : null;
  if (cp && departmentOf(cp, departments).id === dept?.id) return { onSite: true, project: cp, running: null, elsewhere: null };
  if (cp) return { onSite: false, project: null, running: null, elsewhere: { task: null, project: cp, dept: departmentOf(cp, departments) } };
  return { onSite: false, project: null, running: null, elsewhere: null };
}

/**
 * Field's engine on Home (field.md "The Field engine on Home"), one shape for the phone and the web. On site (a Field
 * job's work running): the job, the running task, the pace line (work done vs hours used — jobBurn, the running timer
 * counted), the quick tasks ranked by how you work, each with today's minutes, today's list on the job (Field's tasks
 * only). Not on site: today's jobs to Start, and what's running elsewhere. → { onSite, site, quick, jobs, elsewhere }
 */
export function fieldEngine(dept, data = {}, { clock = null, activeTaskId = null } = {}) {
  const place = fieldPlace(dept, data, { clock, activeTaskId });
  const site = place.onSite ? fieldSite(data, { projectId: place.project.id, activeTaskId: place.running?.id || null, dept }) : null;
  const onSite = !!site?.project;
  const today = localDateStr(data.now || Date.now());
  const quick = onSite ? quickMinutesToday(dept, data, rankQuickTasks(quickTasksFor(dept), data.tasks || [], data.now || Date.now()), today) : [];
  return { onSite, site: onSite ? site : null, quick, jobs: onSite ? [] : fieldTodayJobs(dept, data), elsewhere: place.elsewhere };
}

/** Each quick task with the minutes on it today in this department (Setup 12m): its own tasks (quickTaskId / bucket). */
function quickMinutesToday(dept, data, list, today) {
  const { tasks = [], projects = [], departments = [], departmentMode = true } = data;
  const wd = workDepartments({ tasks, projects, departments, departmentMode });
  return list.map((q) => {
    let ms = 0;
    for (const t of tasks) {
      if (!(t.quickTaskId === q.id || (q.bucket && t.bucket === q.bucket))) continue;
      for (const e of t.timeEntries || []) if (e?.date === today && (e.duration || 0) > 0 && wd.forEntry(e, t)?.id === dept?.id) ms += e.duration;
    }
    return { ...q, todayMs: ms };
  });
}

/**
 * Field — the job you're on: its hours vs the estimate (the running timer counted), and today's tasks on it that are
 * this department's (overdue ones too; the running one marked). `dept` narrows the list (no dept = every task).
 */
export function fieldSite(data = {}, { projectId = null, activeTaskId = null, dept = null } = {}) {
  if (!projectId) return null;
  const today = localDateStr(data.now || Date.now());
  const { tasks = [], projects = [], departments = [], departmentMode = true } = data;
  const project = projects.find((p) => p.id === projectId) || null;
  const wd = dept ? workDepartments({ tasks, projects, departments, departmentMode }) : null;
  const list = tasks.filter((t) => t.projectId === projectId && !t.deletedAt && !t.parentId && !t.bucket && !t.quickTaskId && (!wd || wd.forTask(t)?.id === dept.id)
    && ((t.scheduledDate && t.scheduledDate <= today && open(t)) || t.id === activeTaskId || doneOn(t) === today));
  // the live entry (withLiveTime) isn't in timeSpent yet — count it so the bar moves with the timer
  const liveMs = tasks.filter((t) => t.projectId === projectId && !t.bucket && !t.quickTaskId).reduce((a, t) => a + (t.timeEntries || []).filter((e) => e?.live).reduce((x, e) => x + (e.duration || 0), 0), 0);
  const burn = jobBurn(tasks, projectId);
  if (burn && liveMs) {
    burn.usedMs += liveMs;
    burn.usedShare = burn.estMin ? burn.usedMs / (burn.estMin * 60000) : null;
    const earnedMs = burn.estMin * burn.doneShare * 60000;
    burn.pace = earnedMs > 0 ? burn.usedMs / earnedMs : null;
  }
  return { project, burn, running: activeTaskId ? tasks.find((t) => t.id === activeTaskId) || null : null, list };
}

/**
 * The pace line in words and bar widths (Lj, Oct 11: never "28 of 4 h · 5920% over"): within the estimate "3.5 of 4 h";
 * past it "24 h over a 4 h estimate" with the bar capped — the estimate's share grey, the overflow red; beyond 999 %
 * over the pace the word is "far over". → { donePct, usedText, over, estPct, overPct, fillPct, paceText, paceTone }
 */
export function burnView(burn) {
  if (!burn || !burn.estMin) return null;
  const H = 3600000;
  const h = (ms) => { const v = ms / H; return `${v >= 10 ? Math.round(v) : Math.round(v * 10) / 10} h`; };
  const estMs = burn.estMin * 60000;
  const over = burn.usedMs > estMs;
  const pace = burn.pace;
  const pct = pace != null ? Math.round((pace - 1) * 100) : null;
  return {
    donePct: Math.round(burn.doneShare * 100),
    usedText: over ? `${h(burn.usedMs - estMs)} over a ${h(estMs)} estimate` : `${h(burn.usedMs).replace(' h', '')} of ${h(estMs)}`,
    over,
    fillPct: over ? 100 : Math.max(0, Math.min(100, (burn.usedMs / estMs) * 100)),
    estPct: over ? (estMs / burn.usedMs) * 100 : null,   // where the estimate ends on a capped bar
    overPct: over ? 100 - (estMs / burn.usedMs) * 100 : 0,
    paceText: pace == null ? 'Nothing finished yet — the pace shows once a task with an estimate is done'
      : pct > 999 ? 'Far over the estimate’s pace' : pct > 5 ? `${pct}% over the estimate’s pace` : pct < -5 ? `${-pct}% ahead of the estimate’s pace` : 'On the estimate’s pace',
    paceTone: pace == null ? 'none' : pct > 999 ? 'far' : pct > 5 ? 'over' : 'on',
  };
}

/** General — projects here that belong to another department (with their open task count), and the oldest open tasks. */
export function generalHomes(dept, data = {}) {
  const now = data.now || Date.now();
  const { tasks = [], projects = [], departments = [], departmentMode = true } = data;
  const wd = workDepartments({ tasks, projects, departments, departmentMode });
  const mine = tasks.filter((t) => open(t) && !t.parentId && !t.bucket && wd.forTask(t)?.id === dept?.id);
  const counts = new Map();
  for (const t of mine) if (t.projectId) counts.set(t.projectId, (counts.get(t.projectId) || 0) + 1);
  const homes = projects.filter((p) => counts.has(p.id) && departmentOf(p, departments).id === dept?.id).map((p) => {
    const role = homeSuggestion(p);
    const to = role ? departments.find((x) => deptRole(x) === role && x.id !== dept?.id) : null;
    return to ? { project: p, to, open: counts.get(p.id) } : null;
  }).filter(Boolean).sort((a, b) => b.open - a.open);
  const oldest = mine.filter((t) => num(t.createdAt)).sort((a, b) => num(a.createdAt) - num(b.createdAt)).slice(0, 5).map((t) => ({ task: t, ageDays: ageDays(t, now) }));
  return { homes, oldest };
}

/** Personal — the department's projects (live ones) with their open task count. */
export function deptProjects(dept, data = {}) {
  const { tasks = [], projects = [], departments = [] } = data;
  return projects.filter((p) => departmentOf(p, departments).id === dept?.id && !['completed', 'archived', 'cancelled'].includes(p.status))
    .map((p) => ({ project: p, open: tasks.filter((t) => t.projectId === p.id && open(t) && !t.parentId).length }))
    .sort((a, b) => b.open - a.open);
}
